import React, { useState, useEffect } from 'react';
import { Play, Pause, RotateCcw, Terminal, CheckCircle, AlertTriangle, Shield, User, Cpu, Zap } from 'lucide-react';

export default function InteractiveDemo({ autoPlay = false }) {
  const [isPlaying, setIsPlaying] = useState(autoPlay);
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);

  const demoSteps = [
    {
      agent: 'User',
      icon: User,
      color: 'blue',
      message: 'Deploy customer-service-bot v2.1 to production',
      type: 'request',
      delay: 1500,
    },
    {
      agent: 'Orchestrator',
      icon: Cpu,
      color: 'slate',
      message: 'Analyzing request... Classifying risk level...',
      type: 'processing',
      delay: 2000,
    },
    {
      agent: 'System',
      icon: AlertTriangle,
      color: 'amber',
      message: 'Risk Level: HIGH — Production deployment detected',
      type: 'warning',
      delay: 1500,
    },
    {
      agent: 'CTO Agent',
      icon: Zap,
      color: 'green',
      message: '✓ Architecture validated. Rollback plan verified. Proceeding.',
      type: 'approval',
      delay: 2000,
    },
    {
      agent: 'Nellie (CAO)',
      icon: Shield,
      color: 'purple',
      message: 'Reviewing autonomy constraints and safety policies...',
      type: 'thinking',
      delay: 2500,
    },
    {
      agent: 'Nellie (CAO)',
      icon: Shield,
      color: 'purple',
      message: '✓ Approved with constraints: Human confirmation required for production deployment.',
      type: 'approval',
      delay: 2000,
    },
    {
      agent: 'System',
      icon: Terminal,
      color: 'cyan',
      message: 'Awaiting human confirmation to proceed...',
      type: 'waiting',
      delay: 2000,
    },
    {
      agent: 'Human Operator',
      icon: User,
      color: 'emerald',
      message: '✓ Confirmed — Proceeding with deployment',
      type: 'human',
      delay: 1500,
    },
    {
      agent: 'System',
      icon: CheckCircle,
      color: 'green',
      message: '🚀 Deployment executed successfully. All systems nominal.',
      type: 'success',
      delay: 0,
    },
  ];

  useEffect(() => {
    if (!isPlaying || currentStep >= demoSteps.length) return;

    const timer = setTimeout(() => {
      setCompletedSteps((prev) => [...prev, currentStep]);
      if (currentStep < demoSteps.length - 1) {
        setCurrentStep((prev) => prev + 1);
      } else {
        setIsPlaying(false);
      }
    }, demoSteps[currentStep].delay);

    return () => clearTimeout(timer);
  }, [isPlaying, currentStep, demoSteps.length]);

  const resetDemo = () => {
    setCurrentStep(0);
    setCompletedSteps([]);
    setIsPlaying(false);
  };

  const togglePlay = () => {
    if (currentStep >= demoSteps.length - 1 && completedSteps.length > 0) {
      resetDemo();
      setTimeout(() => setIsPlaying(true), 100);
    } else {
      setIsPlaying(!isPlaying);
    }
  };

  const getTypeStyles = (type) => {
    switch (type) {
      case 'request':
        return 'bg-blue-500/20 border-blue-500/30';
      case 'processing':
        return 'bg-slate-500/20 border-slate-500/30';
      case 'warning':
        return 'bg-amber-500/20 border-amber-500/30';
      case 'approval':
        return 'bg-green-500/20 border-green-500/30';
      case 'thinking':
        return 'bg-purple-500/20 border-purple-500/30 animate-pulse';
      case 'waiting':
        return 'bg-cyan-500/20 border-cyan-500/30';
      case 'human':
        return 'bg-emerald-500/20 border-emerald-500/30';
      case 'success':
        return 'bg-green-500/30 border-green-500/50';
      default:
        return 'bg-slate-500/20 border-slate-500/30';
    }
  };

  const getIconColor = (color) => {
    const colors = {
      blue: 'text-blue-400',
      slate: 'text-slate-400',
      amber: 'text-amber-400',
      green: 'text-green-400',
      purple: 'text-purple-400',
      cyan: 'text-cyan-400',
      emerald: 'text-emerald-400',
    };
    return colors[color] || 'text-slate-400';
  };

  return (
    <div className="bg-slate-900/50 rounded-2xl border border-slate-800 overflow-hidden">
      {/* Terminal Header */}
      <div className="bg-slate-800/50 px-4 py-3 flex items-center justify-between border-b border-slate-700/50">
        <div className="flex items-center gap-3">
          <div className="flex gap-2">
            <div className="w-3 h-3 rounded-full bg-red-500/80" />
            <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
            <div className="w-3 h-3 rounded-full bg-green-500/80" />
          </div>
          <span className="text-sm text-slate-400 font-mono">CAO Governance Flow Demo</span>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={togglePlay}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 text-sm font-medium transition-colors"
          >
            {isPlaying ? (
              <>
                <Pause className="w-4 h-4" /> Pause
              </>
            ) : (
              <>
                <Play className="w-4 h-4" /> {completedSteps.length > 0 ? 'Replay' : 'Play'}
              </>
            )}
          </button>
          <button
            onClick={resetDemo}
            className="p-1.5 rounded-lg hover:bg-slate-700/50 text-slate-400 hover:text-white transition-colors"
          >
            <RotateCcw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Terminal Content */}
      <div className="p-6 min-h-[400px] font-mono text-sm space-y-3">
        {demoSteps.map((step, index) => {
          const isCompleted = completedSteps.includes(index);
          const isCurrent = index === currentStep && isPlaying;
          const isVisible = isCompleted || isCurrent;

          if (!isVisible) return null;

          const StepIcon = step.icon;

          return (
            <div
              key={index}
              className={`flex items-start gap-3 animate-fade-in ${
                isCurrent ? 'animate-pulse' : ''
              }`}
            >
              <div className={`p-2 rounded-lg ${getTypeStyles(step.type)} border`}>
                <StepIcon className={`w-4 h-4 ${getIconColor(step.color)}`} />
              </div>

              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-xs font-semibold ${getIconColor(step.color)}`}>
                    {step.agent}
                  </span>
                  <span className="text-xs text-slate-600">
                    {new Date().toLocaleTimeString()}
                  </span>
                </div>
                <div className={`px-4 py-2 rounded-lg ${getTypeStyles(step.type)} border`}>
                  <span className="text-slate-200">{step.message}</span>
                  {isCurrent && step.type === 'thinking' && (
                    <span className="inline-block ml-2">
                      <span className="animate-pulse">...</span>
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {completedSteps.length === 0 && !isPlaying && (
          <div className="flex items-center justify-center h-64 text-slate-500">
            <div className="text-center">
              <Terminal className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Click Play to see the CAO governance flow in action</p>
            </div>
          </div>
        )}
      </div>

      {/* Progress Bar */}
      <div className="h-1 bg-slate-800">
        <div
          className="h-full bg-gradient-to-r from-blue-500 to-purple-500 transition-all duration-500"
          style={{
            width: `${((completedSteps.length) / demoSteps.length) * 100}%`,
          }}
        />
      </div>
    </div>
  );
}
